import { INGLIS_BLUE } from '../../constants/colors'
import type { FamilyAudienceContent, Site } from '../../types'
import SpokenAT from '../SpokenAT'

const accessChipColor = (chip = '') => {
  if (!chip) return { bg: '#eaf0f9', text: '#003a82', border: '#c2ccd8' }
  const l = chip.toLowerCase()
  if (l.includes('walk') || l.includes('open')) return { bg: '#e7f5ec', text: '#14622f', border: '#b9e0c6' }
  if (l.includes('appointment'))               return { bg: '#fdf3da', text: '#7a5306', border: '#efdca6' }
  if (l.includes('clinical') || l.includes('referral')) return { bg: '#eef1f9', text: '#2f3e74', border: '#cdd5ee' }
  if (l.includes('residential'))               return { bg: '#eaf0f9', text: '#003a82', border: '#c2ccd8' }
  return { bg: '#eaf0f9', text: '#003a82', border: '#c2ccd8' }
}

interface FamilyContentProps {
  data: FamilyAudienceContent
  site: Site
}

export default function FamilyContent({ data, site }: FamilyContentProps) {
  const isHousing = site.category === 'HOUSING'
  const chip = accessChipColor(data.accessChip)

  return (
    <article>
      <h3 style={{ fontSize: '1.18rem', fontWeight: 700, color: 'var(--text-strong)', marginBottom: 14 }}><SpokenAT text={data.headline ?? ''} /></h3>

      {data.accessChip && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: chip.bg, color: chip.text, border: `1.5px solid ${chip.border}`, fontSize: '0.9rem', fontWeight: 600, padding: '8px 14px', borderRadius: 999, marginBottom: 14 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
          {data.accessChip}
        </div>
      )}

      <p style={{ fontSize: '0.97rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 14 }}><SpokenAT text={data.description ?? ''} /></p>

      {data.highlights && data.highlights.length > 0 && (
        <ul style={{ marginBottom: 18, listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.highlights.map((h, i) => (
            <li key={`${i}-${h}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.95rem', color: 'var(--text-strong)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#004090" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}>
                <path d="M20 6 9 17l-5-5"/>
              </svg>
              <SpokenAT text={h} />
            </li>
          ))}
        </ul>
      )}

      {site.category === 'AT_RESOURCE_CENTER' && (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic', margin: '0 0 14px' }}>
          Find the site that serves your area:{' '}
          <a href="https://techowlpa.org/atrc/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
            techowlpa.org/atrc/
          </a>
        </p>
      )}

      <div style={{ borderTop: '1px solid #eef2f7', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {site.phone && (
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 2 }}>Phone</div>
            <a href={`tel:${site.phone}`} style={{ color: INGLIS_BLUE, fontSize: '0.92rem', textDecoration: 'none' }}>{site.phone}</a>
          </div>
        )}
        {site.contact && (
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 2 }}>Email</div>
            <a href={`mailto:${site.contact}`} style={{ color: INGLIS_BLUE, fontSize: '0.92rem', textDecoration: 'none' }}>{site.contact}</a>
            {data.cta && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 2 }}><SpokenAT text={data.cta} /></div>}
          </div>
        )}
      </div>

      {isHousing && site.contact && (
        <a
          href={`mailto:${site.contact}?subject=${encodeURIComponent('Housing Inquiry')}`}
          style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: INGLIS_BLUE, color: '#fff', fontSize: '0.92rem', fontWeight: 600, padding: '12px 16px', borderRadius: 10, textDecoration: 'none' }}
        >
          Learn about applying →
        </a>
      )}
    </article>
  )
}
